import { graphql } from 'react-relay';
import Cookies from 'js-cookie';
import { localStorage } from 'dibs-browser-storage';
import { getGuestId, cookieJarConstants, isMasquerading } from 'dibs-cookie-jar';
import { sourceAttributionMutation } from './sourceAttributionMutation';
import { userProfileMutation } from '../mutations/userProfileMutation';
import guidGenerator from '../helpers/guidGenerator';
import { getMeasurementUnit } from 'dibs-intl/exports/intlSupport';
import { setNonTradeUserToSuspectedTradeMutation } from '../../mutations/setNonTradeUserToSuspectedTradeMutation';
import { getCurrencyPreference } from '../../utils/preferredCurrency';
import serverVars from 'server-vars';
import * as constants from './userStorageConstants';
import {
    getSourceAttribution,
    clearSourceAttribution,
} from 'dibs-visit-tracking/exports/sourceAttribution';

Cookies.defaults = constants.defaultCookieSettings;

// triggered after user is fetched
export function setCookies(user = {}) {
    let userType;
    if (user.isVerifiedTrade) {
        userType = constants.CUSTOMER_TYPE_TRADE;
    } else if (user.isVip) {
        userType = constants.CUSTOMER_TYPE_VIP;
    } else {
        userType = constants.CUSTOMER_TYPE_CONSUMER;
    }
    Cookies.set(constants.ON_SITE_REGISTRATION, true);
    Cookies.set(constants.EVER_LOGGED_IN, userType, { expires: 365 * 5 });
    Cookies.set(constants.USER_TYPE, userType);
}

// set user tokens and user types in cookies.
// also submit guestId to sourceAttribution call.
export function setTokens(payload = {}) {
    const { environment, user, emailToken, userId, userToken } = payload;

    const { profile = {} } = user || {};
    const guestUserId = getGuestId(document.cookie);

    if (guestUserId && !isMasquerading(document.cookie)) {
        sourceAttributionMutation(environment, {
            guestUserId,
            type: constants.GUEST_USER,
            userId,
            userToken,
            emailToken,
            pageVisits: getSourceAttribution(),
        });
        Cookies.remove(cookieJarConstants.GUEST_ID);
    }
    clearSourceAttribution();

    if (userToken) {
        Cookies.set(constants.USER_TOKEN, userToken);
        // Remove email token cookie if it's regular user.
        Cookies.remove(constants.USER_EMAIL_TOKEN);
    }
    if (user) {
        setCookies(user);
    }
    if (profile.email) {
        localStorage.setItem(constants.USER_EMAIL, profile.email);
    }
    if (emailToken) {
        // Set email token cookie just if it's email only user.
        if (!userToken) {
            Cookies.set(constants.USER_EMAIL_TOKEN, emailToken);
        }
        localStorage.setItem(constants.USER_EMAIL_TOKEN, emailToken);
    }
}

export function setGuestId() {
    Cookies.set(cookieJarConstants.GUEST_ID, guidGenerator());
}

export function resetVisitCount() {
    const visitCount = localStorage.getItem(constants.VISIT_COUNT) || {};
    const { regPromptLimit = constants.REG_PROMPT_LIMIT, totalVisitCount = 0 } = visitCount;
    let { sinceRegPrompt = 0 } = visitCount;
    sinceRegPrompt = 0;
    localStorage.setItem(constants.VISIT_COUNT, {
        totalVisitCount,
        sinceRegPrompt,
        regPromptLimit,
    });
}

export function updateProfile(payload) {
    const { environment, userId } = payload;
    const locale = serverVars.get('locale');
    const currency = getCurrencyPreference();
    const measurementUnit = getMeasurementUnit(locale);
    return new Promise(resolve => {
        userProfileMutation(environment, {
            currency,
            measurementUnit,
            userServiceId: userId,
            onCompleted: () => {
                resolve(payload);
            },
            // don't reject - if this mutation fails do not show the user an error
            onError: () => resolve(payload),
        });
    });
}

export function handleWindfallLogin(payload) {
    if (localStorage.getItem(constants.TRADE_WINDFALL_REFERRAL_KEY)) {
        localStorage.deleteItem(constants.TRADE_WINDFALL_REFERRAL_KEY);

        const { environment, userId } = payload;
        setNonTradeUserToSuspectedTradeMutation(environment, {
            userId,
        });
    }
}

export const userTrackingFragment = graphql`
    fragment userStorageActions on User {
        isVerifiedTrade
        isVip
    }
`;
